import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WidgetConfig } from '../../types/widget';

interface WidgetState {
  widgets: WidgetConfig[];
  selectedWidget: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: WidgetState = {
  widgets: [],
  selectedWidget: null,
  isLoading: false,
  error: null,
};

const widgetSlice = createSlice({
  name: 'widgets',
  initialState,
  reducers: {
    setWidgets: (state, action: PayloadAction<WidgetConfig[]>) => {
      state.widgets = action.payload;
    },
    addWidget: (state, action: PayloadAction<WidgetConfig>) => {
      state.widgets.push(action.payload);
    },
    updateWidget: (state, action: PayloadAction<WidgetConfig>) => {
      const index = state.widgets.findIndex(w => w.id === action.payload.id);
      if (index !== -1) {
        state.widgets[index] = action.payload;
      }
    },
    deleteWidget: (state, action: PayloadAction<string>) => {
      state.widgets = state.widgets.filter(w => w.id !== action.payload);
    },
    duplicateWidget: (state, action: PayloadAction<{ widget: WidgetConfig; newId: string }>) => {
      const { widget, newId } = action.payload;
      const duplicatedWidget: WidgetConfig = {
        ...widget,
        id: newId,
        title: `${widget.title} (Copy)`,
        position: {
          ...widget.position,
          z: state.widgets.length,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      state.widgets.push(duplicatedWidget);
    },
    setSelectedWidget: (state, action: PayloadAction<string | null>) => {
      state.selectedWidget = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    moveWidget: (state, action: PayloadAction<{ widgetId: string; newPosition: { x: number; y: number } }>) => {
      const { widgetId, newPosition } = action.payload;
      const widget = state.widgets.find(w => w.id === widgetId);
      if (widget) {
        widget.position = { ...widget.position, ...newPosition };
        widget.updatedAt = new Date();
      }
    },
    resizeWidget: (state, action: PayloadAction<{ widgetId: string; newSize: { width: number; height: number } }>) => {
      const { widgetId, newSize } = action.payload;
      const widget = state.widgets.find(w => w.id === widgetId);
      if (widget) {
        widget.size = { ...widget.size, ...newSize };
        widget.updatedAt = new Date();
      }
    },
    toggleWidgetVisibility: (state, action: PayloadAction<string>) => {
      const widget = state.widgets.find(w => w.id === action.payload);
      if (widget) {
        widget.isVisible = !widget.isVisible;
        widget.updatedAt = new Date();
      }
    },
  },
});

export const {
  setWidgets,
  addWidget,
  updateWidget,
  deleteWidget,
  duplicateWidget,
  setSelectedWidget,
  setLoading,
  setError,
  moveWidget,
  resizeWidget,
  toggleWidgetVisibility,
} = widgetSlice.actions;

export default widgetSlice.reducer;
